import useSWR from 'swr';
import { validate as validateUuid } from 'uuid';
import {
  getRankingResultsRequest,
  getRankingResultsResponse,
} from '@core-api/presentation/presentation/interactive-content/{interactive_content_id}/ranking-results';
import { getFetchResultsCacheKey } from '@mentimeter/core-hooks';
import { useActiveSeriesId } from '@mentimeter/core-hooks/use-ids';
import { usePresentationRealtimeStore } from '@mentimeter/realtime';
import { makeRequest } from '../request-wrapper/make-request';
import { POLLING_INTERVAL } from './constants';
import { getRegion } from './get-region';

interface UseFetchRankingResultsParams {
  adminKey: string;
  interactiveContentId: string;
  shouldFetchResults: boolean;
  sessionId: number | undefined;
}

export const useFetchRankingResults = ({
  adminKey,
  interactiveContentId,
  shouldFetchResults,
  sessionId,
}: UseFetchRankingResultsParams) => {
  const seriesId = useActiveSeriesId();
  const shouldMakeRequest =
    shouldFetchResults &&
    interactiveContentId &&
    validateUuid(interactiveContentId);
  const connectionState = usePresentationRealtimeStore(
    (state) => state.connectionState,
  );

  const { data, mutate, ...rest } = useSWR(
    shouldMakeRequest ? getFetchResultsCacheKey(adminKey, sessionId) : null,
    async () => {
      const region = getRegion(seriesId);
      const response = await fetch(
        makeRequest(getRankingResultsRequest, {
          interactiveContentId,
          ...(region ? { region } : {}),
          ...(sessionId !== undefined ? { session: sessionId } : {}),
        }),
      );
      return await getRankingResultsResponse(response, {
        enableStatusCheck: false,
      });
    },
    {
      shouldRetryOnError: false,
      refreshInterval:
        connectionState === 'failed' || connectionState === 'disconnected'
          ? POLLING_INTERVAL
          : 0,
    },
  );

  return {
    data: shouldMakeRequest ? data : undefined,
    mutate,
    ...rest,
  };
};
